import React, { useState, useEffect, useRef } from 'react';
import { BackPill } from './BackPill';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Lock, ChevronLeft, ChevronRight } from 'lucide-react';
import { ArshHeader } from '../ArshHeader';
import { QuoteBook } from './QuoteBook';
import { TheHarmony, HarmonyTileIcon } from './TheHarmony';
import { BlissGallery } from './BlissGallery';
import TheAgenda from './TheAgenda';
import { TileGuideMenu } from './TileGuideMenu';
import { useNativeBack } from '../../hooks/useHistoryClose';
import { clickVisibleBackButton } from '../../utils/backStack';

/* Tiles without their own back handling: phone back first taps the tile's
   on-screen back (sub-page), otherwise closes the tile. */
const NO_OWN_BACK = new Set(['quote', 'agenda', 'sovereign', 'script', 'aura', 'destiny', 'provenance', 'ledger', 'essence']);
import { TheManifest } from './TheManifest';
import { SovereignsPath } from './SovereignsPath';
import { NotebookEditor } from './NotebookEditor';
import { AuraDashboard } from './AuraDashboard';
import { DestinyCodex, DestinyTileIcon } from './DestinyCodex';
import { Provenance, ProvenanceTileIcon } from './Provenance';
import { ImperialLedger, LedgerTileIcon } from './ImperialLedger';
import { TheOath, OathTileIcon } from './TheOath';
import { TileHelp } from './TileHelp';
import { EssenceVault, EssenceTileIcon } from './EssenceVault';
import { TheEnergy, EnergyTileIcon } from './TheEnergy';
import { ThePower, PowerTileIcon } from './ThePower';
import { ThePeace, PeaceTileIcon } from './ThePeace';
import { TheDivine, DivineTileIcon } from './TheDivine';
import { ThePronoia, PronoiaTileIcon } from './ThePronoia';
import { TheAegis, AegisTileIcon } from './TheAegis';
import { TheFrequency, FrequencyTileIcon } from './TheFrequency';
import { TheTracker, TrackerTileIcon } from './TheTracker';
import { TheRelief, ReliefTileIcon } from './TheRelief';
import { TheEcho, EchoTileIcon } from './TheEcho';
import { TheVision, VisionTileIcon } from './TheVision';
import { TheBlissTimer, BlissTimerTileIcon } from './TheBlissTimer';
import { useDivineBlink } from '../../hooks/useDivineBlink';
import { SponsoredNativeAd } from './SponsoredNativeAd';
import { AtelierCatalog } from '../AtelierCatalog';
import { BACKEND_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { UpgradeModal } from '../premium/UpgradeModal';
import { getEntitlement, onEntitlement } from '../../utils/billing';

/* ═══════════════════════════════════════════════════════════════
   TRY-TAB LAUNCHER — 10 icons (5+5) in top 20%
   Bottom 80% is reserved for AdMob banner / native ads (full slot).
   ═══════════════════════════════════════════════════════════════ */

const ImageIcon = ({ src, size = 26, glow }) => (
  <img
    src={src}
    alt=""
    style={{
      width: size, height: size, objectFit: 'cover',
      borderRadius: '50%',
      filter: `drop-shadow(0 0 8px ${glow})`,
    }}
  />
);

const SigPen      = ({ size = 26 }) => <ImageIcon src="/icons/signature_pen.png"   size={size} glow="rgba(212,175,55,0.6)" />;
/* Agenda — gold ledger grid with a ticked row and a progress ring. */
const AgendaIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" fill="none" style={{ filter: 'drop-shadow(0 0 6px rgba(220,20,60,0.7))' }}>
    <defs>
      <linearGradient id="agendaGold" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FFE9A0"/><stop offset="1" stopColor="#B47828"/>
      </linearGradient>
    </defs>
    <rect x="3" y="5" width="17" height="18" rx="2.5" fill="#1a1408" stroke="url(#agendaGold)" strokeWidth="1.4"/>
    <path d="M3 10 H20" stroke="url(#agendaGold)" strokeWidth="1.1"/>
    <rect x="6" y="12.5" width="3.2" height="3.2" rx="0.7" fill="#D4AF37"/>
    <path d="M6.6 14.1 l0.9 0.9 l1.4 -1.6" stroke="#1a1408" strokeWidth="0.9" strokeLinecap="round"/>
    <rect x="11" y="12.5" width="3.2" height="3.2" rx="0.7" stroke="#D4AF37" strokeWidth="0.8"/>
    <rect x="6" y="17.5" width="3.2" height="3.2" rx="0.7" stroke="#D4AF37" strokeWidth="0.8"/>
    <rect x="11" y="17.5" width="3.2" height="3.2" rx="0.7" fill="#10B981"/>
    <circle cx="21" cy="19" r="4.6" fill="#0c0a06" stroke="rgba(255,255,255,0.15)" strokeWidth="1.6"/>
    <circle cx="21" cy="19" r="4.6" stroke="#F5D97A" strokeWidth="1.6" strokeDasharray="18 29" transform="rotate(-90 21 19)" strokeLinecap="round"/>
  </svg>
);
const VaultDragon = ({ size = 26 }) => <ImageIcon src="/icons/empire_seal.png"     size={size} glow="rgba(212,175,55,0.7)" />;
const SovereignsIcon = ({ size = 26 }) => <ImageIcon src="/icons/sovereign_chronicle.png" size={size} glow="rgba(139,63,191,0.75)" />;

const ManifestIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.7))' }}>
    <defs>
      <linearGradient id="mfg" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#F1D27A"/>
        <stop offset="1" stopColor="#B7892D"/>
      </linearGradient>
    </defs>
    <path d="M13 2 L4 14 H11 L9 22 L20 9 H13 Z" fill="url(#mfg)" stroke="#0a0a0a" strokeWidth="0.6"/>
  </svg>
);

/* Royal Feather — gold-foil quill matching the Manifest icon's aesthetic.
   Custom SVG with vertical gold gradient, dark imperial outline, vane spine,
   and a delicate diagonal flourish. Replaces the lucide red-stroke Feather. */
const RoyalFeather = ({ size = 26 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    style={{ filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.7))' }}
  >
    <defs>
      <linearGradient id="rfg" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%"   stopColor="#FFF1B8"/>
        <stop offset="40%"  stopColor="#F1D27A"/>
        <stop offset="100%" stopColor="#8B6B1F"/>
      </linearGradient>
      <linearGradient id="rfgEdge" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%"  stopColor="#7A5418"/>
        <stop offset="100%" stopColor="#3A2410"/>
      </linearGradient>
    </defs>
    {/* Vane: a feather sweeping from upper-right to lower-left */}
    <path
      d="M20.5 3.2
         C 15 2.5, 10 5, 7 9
         C 4.5 12, 3.5 16, 3.5 20.5
         L 5.5 20.5
         C 10 16, 14 13, 19.2 9.5
         C 21 8.2, 21.5 5.5, 20.5 3.2 Z"
      fill="url(#rfg)"
      stroke="url(#rfgEdge)"
      strokeWidth="0.7"
      strokeLinejoin="round"
    />
    {/* Center spine (rachis) */}
    <path
      d="M20 3.7 L 4.6 20.4"
      stroke="#3A2410"
      strokeWidth="0.7"
      strokeLinecap="round"
    />
    {/* Subtle vane ribs to suggest barbs */}
    <g stroke="#7A5418" strokeWidth="0.35" strokeLinecap="round" opacity="0.55">
      <path d="M17.5 5.5 L 15 8.2" />
      <path d="M14.8 7.4 L 12 10.4" />
      <path d="M12.2 9.6 L 9.2 12.8" />
      <path d="M9.7 12   L 6.8 15.4" />
      <path d="M7.4 14.5 L 5   18" />
    </g>
    {/* Tip glint */}
    <circle cx="20" cy="3.6" r="0.55" fill="#FFF6D8" />
  </svg>
);

/* Empire Script — rolled scroll with gold-foil edges and a faint quill,
   bold enough to read at 26px. Custom SVG so it matches the Manifest /
   Royal-Feather treatment instead of a plain lucide stroke icon. */
const EmpireScriptIcon = ({ size = 26 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    style={{ filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.7))' }}
  >
    <defs>
      <linearGradient id="esg" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%"   stopColor="#FFF1B8"/>
        <stop offset="40%"  stopColor="#F1D27A"/>
        <stop offset="100%" stopColor="#8B6B1F"/>
      </linearGradient>
      <linearGradient id="esgEdge" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%"  stopColor="#7A5418"/>
        <stop offset="100%" stopColor="#3A2410"/>
      </linearGradient>
    </defs>
    {/* Top scroll roll */}
    <ellipse cx="12" cy="4.5" rx="6.5" ry="1.6"
      fill="url(#esg)" stroke="url(#esgEdge)" strokeWidth="0.6" />
    {/* Parchment body */}
    <path
      d="M5.5 4.5
         L 5.5 18.5
         C 5.5 19.6, 8.4 20.5, 12 20.5
         C 15.6 20.5, 18.5 19.6, 18.5 18.5
         L 18.5 4.5 Z"
      fill="url(#esg)"
      stroke="url(#esgEdge)"
      strokeWidth="0.7"
      strokeLinejoin="round"
    />
    {/* Bottom roll */}
    <ellipse cx="12" cy="18.5" rx="6.5" ry="1.6"
      fill="url(#esg)" stroke="url(#esgEdge)" strokeWidth="0.6" />
    {/* Inscribed lines */}
    <g stroke="#3A2410" strokeWidth="0.55" strokeLinecap="round" opacity="0.7">
      <path d="M8 8.5 L 16 8.5"/>
      <path d="M8 11   L 15 11"/>
      <path d="M8 13.5 L 16 13.5"/>
      <path d="M8 16   L 14 16"/>
    </g>
    {/* Tiny wax-seal dot (ruby) */}
    <circle cx="16.5" cy="16.4" r="1.1" fill="#A8201A" stroke="#3A2410" strokeWidth="0.35"/>
  </svg>
);

/* Aura — concentric brushed-titanium ring with a translucent
   violet/electric-blue plasma sphere at the centre.  Matches the
   Aura Codex artifact shown in the dashboard, scaled down to 26px. */
const AuraTileIcon = ({ size = 26 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 40 40"
    style={{ filter: 'drop-shadow(0 0 6px rgba(93,212,255,0.7))' }}
  >
    <defs>
      <radialGradient id="auraOrb" cx="35%" cy="32%" r="60%">
        <stop offset="0%"  stopColor="#FFFFFF"/>
        <stop offset="22%" stopColor="#9CE0FF"/>
        <stop offset="55%" stopColor="#3580E6"/>
        <stop offset="100%" stopColor="#0E0830"/>
      </radialGradient>
      <linearGradient id="auraRing" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%"  stopColor="#F4F5F8"/>
        <stop offset="50%" stopColor="#7F8189"/>
        <stop offset="100%" stopColor="#D4D5D9"/>
      </linearGradient>
    </defs>
    {/* outer chrome ring */}
    <circle cx="20" cy="20" r="18" fill="none"
      stroke="url(#auraRing)" strokeWidth="2.2" />
    {/* thin accent ring */}
    <circle cx="20" cy="20" r="14.5" fill="none"
      stroke="#5DD4FF" strokeWidth="0.6" opacity="0.65" />
    {/* plasma sphere */}
    <circle cx="20" cy="20" r="11" fill="url(#auraOrb)" />
    {/* glint */}
    <ellipse cx="16" cy="15" rx="3" ry="1.7" fill="#FFFFFF" opacity="0.55" />
  </svg>
);

const TILES = [
  // ── Row 1 (per user spec Feb 2026): Essence · Vault · Sovereign · Quote · Ledger · Oath
  { id: 'essence',    label: 'Essence',    accent: '#D4AF37', glow: 'rgba(212,175,55,0.55)',  Icon: EssenceTileIcon,   locked: false },  // gold — collectible cards
  { id: 'vault',      label: 'Vault',      accent: '#1FAA8F', glow: 'rgba(31,170,143,0.55)',  Icon: VaultDragon,       locked: false },  // emerald — treasure
  { id: 'sovereign',  label: 'Sovereign',  accent: '#8B3FBF', glow: 'rgba(139,63,191,0.6)',   Icon: SovereignsIcon,    locked: false },  // imperial purple
  { id: 'quote',      label: 'Quote',      accent: '#B22222', glow: 'rgba(178,34,34,0.55)',   Icon: RoyalFeather,      locked: false },  // crimson
  { id: 'ledger',     label: 'Ledger',     accent: '#A0651F', glow: 'rgba(160,101,31,0.55)',  Icon: LedgerTileIcon,    locked: false },  // oxblood-bronze
  { id: 'oath',       label: 'Oath',       accent: '#E0B4FF', glow: 'rgba(185,120,255,0.6)',  Icon: OathTileIcon,      locked: false },  // gold seal · violet core
  // ── Row 2: Aura · Power · Fuel · Force · Energy · Aegis
  { id: 'aura',       label: 'Aura',       accent: '#5DD4FF', glow: 'rgba(93,212,255,0.65)',  Icon: AuraTileIcon,      locked: false },  // aqua
  { id: 'power',      label: 'Power',      accent: '#FF6478', glow: 'rgba(255,100,120,0.55)', Icon: PowerTileIcon,     locked: false },  // ruby
  { id: 'energy',     label: 'Fuel',       accent: '#5BE08A', glow: 'rgba(91,224,138,0.55)',  Icon: EnergyTileIcon,    locked: false },  // emerald
  { id: 'peace',      label: 'Force',      accent: '#5DD4FF', glow: 'rgba(93,212,255,0.55)',  Icon: PeaceTileIcon,     locked: false },  // sapphire
  { id: 'divine',     label: 'Energy',     accent: '#B978FF', glow: 'rgba(185,120,255,0.55)', Icon: DivineTileIcon,    locked: false },  // amethyst
  { id: 'aegis',      label: 'Aegis',      accent: '#FFB265', glow: 'rgba(255,178,101,0.6)',  Icon: AegisTileIcon,     locked: false },  // orange
];

/* ── Second half of the launcher grid (rows 3-4).  Rendered directly
   after TILES in the same unified 4×6 grid. */
const TILES_MORE = [
  // ── Row 3: Destiny · Vision · Manifest · Pronoia · Frequency · Relief
  { id: 'destiny',    label: 'Destiny',    accent: '#7C9BFF', glow: 'rgba(124,155,255,0.6)',  Icon: DestinyTileIcon,   locked: false },
  { id: 'vision',     label: 'Vision',     accent: '#E9C77A', glow: 'rgba(233,199,122,0.6)',  Icon: VisionTileIcon,    locked: false },
  { id: 'manifest',   label: 'Manifest',   accent: '#FFC83D', glow: 'rgba(255,200,61,0.7)',   Icon: ManifestIcon,      locked: false },
  { id: 'pronoia',    label: 'Pronoia',    accent: '#FFD98A', glow: 'rgba(255,217,138,0.55)', Icon: PronoiaTileIcon,   locked: false },
  { id: 'frequency',  label: 'Frequency',  accent: '#7DD3C0', glow: 'rgba(125,211,192,0.55)', Icon: FrequencyTileIcon, locked: false },
  { id: 'relief',     label: 'Relief',     accent: '#E9C77A', glow: 'rgba(233,199,122,0.6)',  Icon: ReliefTileIcon,    locked: false },
  // ── Row 4: Tracker · Agenda · BlissTimer · Harmony · Echo · Mosaic
  { id: 'tracker',    label: 'Tracker',    accent: '#E9C77A', glow: 'rgba(233,199,122,0.6)',  Icon: TrackerTileIcon,   locked: false },
  { id: 'agenda',     label: 'Agenda',     accent: '#DC143C', glow: 'rgba(220,20,60,0.6)',    Icon: AgendaIcon,        locked: false },
  { id: 'book',       label: 'BlissTimer', accent: '#00A6FF', glow: 'rgba(0,166,255,0.55)',   Icon: BlissTimerTileIcon,locked: false },
  { id: 'signature',  label: 'Harmony',    accent: '#5A4FCF', glow: 'rgba(90,79,207,0.55)',   Icon: HarmonyTileIcon,   locked: false },
  { id: 'echo',       label: 'Echo',       accent: '#B57EDC', glow: 'rgba(181,126,220,0.55)', Icon: EchoTileIcon,      locked: false },
  { id: 'provenance', label: 'Mosaic',     accent: '#D87979', glow: 'rgba(216,121,121,0.55)', Icon: ProvenanceTileIcon,locked: false },
];

const Tile = ({ tile, onClick, badge, blink, premiumLock }) => {
  const { Icon, accent, glow, label, locked, id } = tile;
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.92 }}
      whileHover={!locked ? { scale: 1.04 } : {}}
      onClick={onClick}
      disabled={locked}
      data-testid={`try-tile-${id}`}
      className={`relative flex flex-col items-center justify-center gap-1 active:opacity-80 ${blink ? 'divine-tile-blink' : ''}`}
      data-premium-locked={premiumLock ? 'true' : undefined}
      style={{
        opacity: premiumLock ? 0.45 : 1,
        aspectRatio: '5 / 4',
        borderRadius: 14,
        background: locked
          ? 'linear-gradient(160deg, rgba(255,255,255,0.025), rgba(255,255,255,0.005))'
          : `linear-gradient(160deg, rgba(255,255,255,0.06), rgba(0,0,0,0.4))`,
        border: locked ? '1px solid rgba(255,255,255,0.06)' : `1.2px solid ${accent}`,
        boxShadow: locked ? 'none' : `0 0 18px ${glow}, inset 0 0 12px rgba(255,255,255,0.04)`,
      }}
    >
      {!locked && (
        <span
          className="absolute top-1 left-1 right-1 h-2.5 rounded-full pointer-events-none"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.25), transparent)',
            filter: 'blur(2px)',
          }}
        />
      )}
      {/* Tile progress badge (e.g. Provenance "2/5" mastered count) */}
      {badge && (
        <span
          className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center font-['Outfit'] text-[8px] font-semibold pointer-events-none"
          style={{
            background: accent,
            color: '#0a0610',
            boxShadow: `0 0 8px ${glow}`,
          }}
          data-testid={`try-tile-${id}-badge`}
        >
          {badge}
        </span>
      )}
      {/* Icon — image-based + custom-SVG tiles render their own glyph,
          remaining (lucide) tiles get the color/stroke-width treatment. */}
      {id === 'quote' || id === 'signature' || id === 'agenda' || id === 'manifest' || id === 'vault' || id === 'sovereign' || id === 'script' || id === 'aura' || id === 'destiny' || id === 'provenance' || id === 'ledger' || id === 'oath' || id === 'peace' || id === 'divine' || id === 'pronoia' ? (
        <Icon size={22} />
      ) : (
        <Icon
          size={18}
          strokeWidth={1.6}
          style={{
            color: locked ? 'rgba(255,255,255,0.18)' : accent,
            filter: locked ? 'none' : `drop-shadow(0 0 6px ${glow})`,
          }}
        />
      )}
      {/* Label — sized to guarantee the longest word ("Provenance") fits
          fully inside the tile on narrow phones. We drop letter-spacing,
          shrink the font, and remove `truncate` so no word is cut. */}
      <span
        className="font-['Outfit'] uppercase w-full text-center leading-none"
        style={{
          color: locked ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.78)',
          fontSize: 7,
          letterSpacing: '0.01em',
          padding: '0 2px',
          whiteSpace: 'nowrap',
          overflow: 'visible',
        }}
      >
        {label}
      </span>
    </motion.button>
  );
};

export const TryLauncher = ({ onOpenMuseum, active = true }) => {
  const adFrameRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [ent, setEnt] = useState(null);
  const { user } = useAuth();
  useEffect(() => onEntitlement(setEnt), []);
  useEffect(() => { getEntitlement().then(setEnt); }, [user?.email]);
  const paywalled = (id) => !!(ent && ent.paywall_enabled && !ent.entitled && ent.locked_tiles.includes(id));
  const [open, setOpen] = useState(null); // 'art' | 'quote' | 'signature' | 'agenda' | 'script' | 'provenance' | null
  const [atelierOpen, setAtelierOpen] = useState(false);
  const navigate = useNavigate();
  const { blink: divineBlink } = useDivineBlink();

  /* Codex alarm listener — when a codex alarm is firing we blink BOTH
     the Aura tile and the matching Bliss tile so the whole chain lights
     up (matches Aura Codex → inner ball → outer tile requirement). */
  const [codexAlarm, setCodexAlarm] = useState(() => {
    try { return localStorage.getItem('codex_alarm_active') || null; } catch (_) { return null; }
  });
  useEffect(() => {
    const onAlarm = (e) => setCodexAlarm(e.detail?.pillarId || null);
    window.addEventListener('codex-alarm', onAlarm);
    return () => window.removeEventListener('codex-alarm', onAlarm);
  }, []);
  // Pillar id → tile id mapping. Fuel pillar → 'energy' tile (emerald
  // "Fuel" tile), Force → 'peace' tile, Energy → 'divine' tile.
  const codexTileMap = {
    power: 'power', fuel: 'energy', force: 'peace',
    energy: 'divine', destiny: 'destiny', aegis: 'aegis',
  };
  const alarmingTileId = codexAlarm ? codexTileMap[codexAlarm] : null;


  useNativeBack(NO_OWN_BACK.has(open), () => { if (!clickVisibleBackButton()) setOpen(null); });
  useNativeBack(atelierOpen, () => setAtelierOpen(false));
  useNativeBack(menuOpen, () => setMenuOpen(false));

  useEffect(() => {
    const on = !!(open || atelierOpen || menuOpen);
    document.body.classList.toggle('bliss-tile-open', on);
    return () => document.body.classList.remove('bliss-tile-open');
  }, [open, atelierOpen, menuOpen]);

  return (
    <div className="flex flex-col h-full bg-[#05050A] overflow-hidden">
      {/* While a tile is open the launcher underneath is fully covered:
          hide it (no paint) and pause its animations so every tile gets
          the whole frame budget. */}
      <div className="contents" data-launcher-bg style={open || atelierOpen || menuOpen ? { visibility: 'hidden' } : undefined}>
      <ArshHeader right={
        <button onClick={() => setMenuOpen(true)} data-testid="inscribe-menu-btn"
          className="menu-breathe px-2 rounded-full font-['Cinzel'] uppercase active:scale-95 my-1.5 mr-1"
          style={{ border: '1px solid rgba(212,175,55,0.55)', background: 'rgba(212,175,55,0.08)', color: '#F5D97A',
            fontSize: 8, lineHeight: '16px', letterSpacing: '0.18em', paddingLeft: 'calc(0.5rem + 0.18em)' }}>
          Menu
        </button>
      }/>
      <style>{`
        @keyframes menuBreathe {
          0%, 100% { box-shadow: 0 0 3px rgba(212,175,55,0.15); border-color: rgba(212,175,55,0.4); }
          50% { box-shadow: 0 0 10px rgba(245,217,122,0.55), 0 0 18px rgba(212,175,55,0.25); border-color: rgba(245,217,122,0.9); }
        }
        .menu-breathe { animation: menuBreathe 4s ease-in-out infinite; }
        @keyframes divineTileBlink {
          0%, 100% {
            box-shadow: 0 0 18px rgba(233,199,122,0.55), inset 0 0 12px rgba(255,255,255,0.04);
            border-color: #E9C77A;
          }
          50% {
            box-shadow: 0 0 30px rgba(255,246,208,0.85), 0 0 55px rgba(233,199,122,0.55), inset 0 0 18px rgba(255,246,208,0.28);
            border-color: #FFF6D0;
          }
        }
        .divine-tile-blink { animation: divineTileBlink 1.4s ease-in-out infinite; }
      `}</style>

      {/* UNIFIED LAUNCHER GRID — 4 rows × 6 cols, single container so the
          vertical gap between rows 1↔2, 2↔3, 3↔4 is IDENTICAL (was
          previously split into two grids with a container break between
          rows 2↔3 that visually compressed rows 3↔4). Fluid vh-based
          height and Tailwind sm/md breakpoints keep it responsive on
          phones, tablets, and laptops. */}
      <div
        className="flex-shrink-0 px-2 sm:px-3 md:px-4"
        style={{ flex: '1 1 auto', minHeight: 240, maxHeight: '38vh' }}
        data-testid="try-launcher-grid"
      >
        <div className="h-full grid grid-cols-6 grid-rows-4 gap-1.5 sm:gap-2 md:gap-2.5 py-1.5 sm:py-2">
          {[...TILES, ...TILES_MORE].map((t) => (
            <Tile
              key={t.id}
              tile={t}
              premiumLock={paywalled(t.id)}
              blink={
                (t.id === 'divine' && divineBlink) ||
                (t.id === 'aura' && !!codexAlarm) ||
                (t.id === alarmingTileId)
              }
              onClick={() => {
                if (t.locked) return;
                if (paywalled(t.id)) { setUpgradeOpen(true); return; }
                // Vault tile launches the full Imperium museum directly
                // (no sub-modal — uses the parent's full-screen route).
                if (t.id === 'vault') {
                  onOpenMuseum && onOpenMuseum();
                  return;
                }
                setOpen(t.id);
              }}
            />
          ))}
        </div>
      </div>

      {/* BLISS WORLD ENTRY BANNER — gold-framed rectangular tab (same size
          as the archived Atelier banner) whose border continuously breathes
          through the 8 pillar colours back-to-back. Tapping it opens the
          Discover experience. The Atelier catalog is retained (imported +
          overlay code) but is no longer triggered from this button — it
          will be brought back in a future release. */}
      <style>{`
        @keyframes blissWorldBeam {
          0%   { border-color: #D4AF37; box-shadow: 0 0 12px rgba(212,175,55,0.55), inset 0 0 8px rgba(212,175,55,0.10); }
          12.5%{ border-color: #C8A95E; box-shadow: 0 0 12px rgba(200,169,94,0.55), inset 0 0 8px rgba(200,169,94,0.10); }
          25%  { border-color: #0F52BA; box-shadow: 0 0 14px rgba(15,82,186,0.60),  inset 0 0 8px rgba(15,82,186,0.10); }
          37.5%{ border-color: #DC143C; box-shadow: 0 0 14px rgba(220,20,60,0.60),  inset 0 0 8px rgba(220,20,60,0.10); }
          50%  { border-color: #10B981; box-shadow: 0 0 14px rgba(16,185,129,0.60), inset 0 0 8px rgba(16,185,129,0.10); }
          62.5%{ border-color: #E5C44D; box-shadow: 0 0 12px rgba(229,196,77,0.55), inset 0 0 8px rgba(229,196,77,0.10); }
          75%  { border-color: #8B5CF6; box-shadow: 0 0 14px rgba(139,92,246,0.60), inset 0 0 8px rgba(139,92,246,0.10); }
          87.5%{ border-color: #2A9BD8; box-shadow: 0 0 14px rgba(42,155,216,0.60), inset 0 0 8px rgba(42,155,216,0.10); }
          100% { border-color: #D4AF37; box-shadow: 0 0 12px rgba(212,175,55,0.55), inset 0 0 8px rgba(212,175,55,0.10); }
        }
        .bliss-world-beam {
          border: 2px solid #D4AF37;
          animation: blissWorldBeam 12s linear infinite;
        }
        /* Text + arrow colour cycles in perfect sync with the border beam
           so the whole banner breathes as one living object. */
        @keyframes blissWorldTextHue {
          0%   { color: #FFE9A0; }
          12.5%{ color: #E8D08A; }
          25%  { color: #5CA0FF; }
          37.5%{ color: #FF6B85; }
          50%  { color: #4EE3B4; }
          62.5%{ color: #FFEAA0; }
          75%  { color: #C6A5FF; }
          87.5%{ color: #6BC8F0; }
          100% { color: #FFE9A0; }
        }
        .bliss-world-hue { animation: blissWorldTextHue 12s linear infinite; }
      `}</style>
      <div className="flex-shrink-0 px-3 pt-2">
        <button
          type="button"
          onClick={() => navigate('/discover')}
          className="relative w-full rounded-xl overflow-hidden bliss-world-beam"
          style={{
            background:
              'radial-gradient(ellipse at 20% 20%, rgba(197,160,89,0.07) 0%, rgba(0,0,0,0) 60%), ' +
              'linear-gradient(180deg, #0c0710 0%, #050308 100%)',
          }}
          data-testid="bliss-world-entry-banner"
          aria-label="Enter Bliss World — Society of Successful Empires">
          <div className="px-3 py-2.5 flex items-center gap-2">
            {/* Left triple-chevron pointing at the title */}
            <span
              className="bliss-world-hue flex-shrink-0 flex items-center"
              aria-hidden
              data-testid="bliss-world-arrows-left">
              <ChevronRight size={18} strokeWidth={2.5} style={{ marginRight: -8 }} />
              <ChevronRight size={18} strokeWidth={2.5} style={{ marginRight: -8 }} />
              <ChevronRight size={18} strokeWidth={2.5} />
            </span>
            {/* Centered title block */}
            <div className="flex-1 min-w-0 flex flex-col items-center justify-center text-center">
              <p
                className="bliss-world-hue font-['Cinzel'] uppercase leading-tight"
                style={{
                  fontSize: 15,
                  letterSpacing: '0.22em',
                  textShadow: '0 0 10px rgba(197,160,89,0.55)',
                  whiteSpace: 'nowrap',
                }}
                data-testid="bliss-world-title">
                BLISS WORLD
              </p>
              <p
                className="bliss-world-hue font-['Cormorant_Garamond'] italic mt-0.5 leading-tight"
                style={{
                  fontSize: 11,
                  letterSpacing: '0.05em',
                  opacity: 0.9,
                  whiteSpace: 'nowrap',
                }}
                data-testid="bliss-world-subtitle">
                Society of Successful Empires
              </p>
            </div>
            {/* Right triple-chevron pointing at the title */}
            <span
              className="bliss-world-hue flex-shrink-0 flex items-center"
              aria-hidden
              data-testid="bliss-world-arrows-right">
              <ChevronLeft size={18} strokeWidth={2.5} />
              <ChevronLeft size={18} strokeWidth={2.5} style={{ marginLeft: -8 }} />
              <ChevronLeft size={18} strokeWidth={2.5} style={{ marginLeft: -8 }} />
            </span>
          </div>
        </button>
      </div>

      {/* Single gold outline framing the AdMob ad slot below Atelier.
          Empty on web preview; the AdMob 300×250 banner floats over this
          frame on the installed APK, filling it perfectly. */}
      <div aria-hidden className="flex-shrink-0 flex items-center justify-center px-4 pt-3 pb-2">
        <div className="w-[300px] h-[250px] max-w-full rounded-[14px] relative"
          style={{
            border: '1px solid rgba(212,175,55,0.55)',
            boxShadow: '0 0 24px rgba(212,175,55,0.12), inset 0 0 30px rgba(212,175,55,0.05)',
            background: 'radial-gradient(ellipse at center, rgba(20,15,10,0.4) 0%, rgba(0,0,0,0) 70%)',
          }}
          ref={adFrameRef}
          data-testid="admob-outline">
        </div>
      </div>

      {/* AdMob Native Ad lifecycle mount — invisible React component.
          On installed APK: shows a 300×250 Google banner at BOTTOM_CENTER
          (above the bottom nav) while the Inscribe tab is open; auto-hides
          when navigating away. On web/preview: no-op — outline above stays. */}
      <SponsoredNativeAd enabled={active && !open && !atelierOpen && !menuOpen} anchorRef={adFrameRef}/>
      </div>

      {/* Modals — rendered inside a layer that sits between the phone's
          status bar and navigation bar, so every tile fits the screen. */}
      {upgradeOpen && (
        <UpgradeModal onClose={() => setUpgradeOpen(false)} lockedTiles={ent?.locked_tiles || []}
          icons={Object.fromEntries([...TILES, ...TILES_MORE].map((t) => [t.id, t.Icon]))}/>
      )}
      {(open || atelierOpen || menuOpen) && (
      <div className="bliss-tile-layer" data-testid="tile-layer">
      {menuOpen && (
        <TileGuideMenu
          icons={Object.fromEntries([...TILES, ...TILES_MORE].map((t) => [t.id, t.Icon]))}
          onClose={() => setMenuOpen(false)}
          onOpenTile={(id) => {
            setMenuOpen(false);
            if (id === 'vault') { onOpenMuseum && onOpenMuseum(); return; }
            if (paywalled(id)) { setUpgradeOpen(true); return; }
            setOpen(id);
          }}
          onOpenPremium={() => { setMenuOpen(false); setUpgradeOpen(true); }}/>
      )}
      {open === 'quote' && <QuoteBook onClose={() => setOpen(null)} />}
      {open === 'signature' && <TheHarmony onClose={() => setOpen(null)} />}
      {open === 'agenda' && <TheAgenda onClose={() => setOpen(null)} />}
      {open === 'manifest' && <TheManifest onClose={() => setOpen(null)} />}
      {open === 'sovereign' && <SovereignsPath onClose={() => setOpen(null)} />}
      {open === 'script' && (
        <NotebookEditor
          mode="note"
          user={user}
          onClose={() => setOpen(null)}
          onSaved={() => { /* feed reloads internally */ }}
        />
      )}
      {open === 'aura' && <AuraDashboard onClose={() => setOpen(null)} />}
      {open === 'destiny' && <DestinyCodex onClose={() => setOpen(null)} />}
      {open === 'provenance' && <Provenance onClose={() => setOpen(null)} />}
      {open === 'ledger' && <ImperialLedger onClose={() => setOpen(null)} />}
      {open === 'oath' && <TheOath onClose={() => setOpen(null)} />}
      {open && <TileHelp key={open} tileId={open} />}
      {open === 'essence' && <EssenceVault onClose={() => setOpen(null)} />}
      {open === 'energy' && <TheEnergy onClose={() => setOpen(null)} />}
      {open === 'power' && <ThePower onClose={() => setOpen(null)} />}
      {open === 'peace' && <ThePeace onClose={() => setOpen(null)} />}
      {open === 'divine' && <TheDivine onClose={() => setOpen(null)} />}
      {open === 'pronoia' && <ThePronoia onClose={() => setOpen(null)} />}
      {open === 'aegis' && <TheAegis onClose={() => setOpen(null)} />}
      {open === 'frequency' && <TheFrequency onClose={() => setOpen(null)} />}
      {open === 'tracker' && <TheTracker onClose={() => setOpen(null)} />}
      {open === 'relief' && <TheRelief onClose={() => setOpen(null)} />}
      {open === 'echo' && <TheEcho onClose={() => setOpen(null)} />}
      {open === 'vision' && <TheVision onClose={() => setOpen(null)} />}
      {open === 'book' && <TheBlissTimer onClose={() => setOpen(null)} />}

      {/* Atelier full-screen overlay — dedicated separate experience, no ads */}
      {atelierOpen && (
        <div className="fixed inset-0 z-[300] flex flex-col" style={{ background: '#050308' }}
          data-testid="atelier-overlay">
          <div className="flex items-center justify-between px-3 py-2.5 border-b flex-shrink-0"
            style={{ borderColor: 'rgba(197,160,89,0.18)' }}>
            <BackPill onClick={() => setAtelierOpen(false)} accent="#D4AF37" testId="atelier-close"/>
            <span className="w-12"/>
          </div>
          <div className="flex-1 min-h-0">
            <AtelierCatalog/>
          </div>
        </div>
      )}
      </div>
      )}
    </div>
  );
};
